import { useCallback, useMemo, useRef, useState } from "react";
import {
  filterSections,
  useRecipientSections,
  type OptionSection,
} from "@/v5/components/RecipientPicker";
import type { RecipientOption } from "@/v5/components/RecipientPicker";

/**
 * Inline "@" tagging for a plain <textarea>.
 *
 * The practice already tags colleagues through RecipientPicker's dialog. That
 * produces a token array (`"12"`, `"role_5"`, `"coord"`) which the server
 * expands in CommLogRecipientService. This hook produces the SAME tokens by
 * typing instead of clicking, so nothing downstream changes.
 *
 * The two halves — the tokens that drive notification, and the text the reader
 * sees — are separate values, and keeping them honest is the whole job here:
 *
 *   - Picking a name inserts "@Jane Doe" into the text AND records the token.
 *   - Deleting that text again drops the token, so the note does not quietly
 *     notify someone whose name is no longer in it.
 *
 * Tokens chosen in the dialog are NOT tracked here, so they are never
 * reconciled away by an edit to the body — they have no text to match against.
 * The composer merges both sources before sending.
 */

/** A tag that came from typing, and the text in the body that justifies it. */
export interface InlineMention {
  token: string;
  text: string;
}

/**
 * Opens on "@" at a word boundary. The leading (?:^|\s) is what keeps an email
 * address from triggering the menu: in "patient@example.com" the "@" is
 * preceded by "t", so there is no match.
 *
 * The character class deliberately excludes the space, so the run ends at the
 * first one. That also means an already-inserted "@Jane Doe " cannot re-open
 * the menu when the caret passes over it.
 */
const TRIGGER = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u;

/**
 * What gets typed into the note body for an option.
 *
 * The picker labels groups "Front Desk (User Group)" and plan roles
 * "Coordinator (Plan)" to disambiguate them in a checklist. Inside a sentence
 * that parenthetical reads as noise, so it is dropped — "@Front Desk" is what
 * a person would have written.
 */
export function mentionTextFor(option: RecipientOption): string {
  return `@${option.label.replace(/\s*\((?:User Group|Plan)\)\s*$/u, "")}`;
}

interface UseMentionAutocompleteArgs {
  value: string;
  setValue: (next: string) => void;
  planId?: string | number | null;
  disabled?: boolean;
}

export function useMentionAutocomplete({
  value,
  setValue,
  planId,
  disabled,
}: UseMentionAutocompleteArgs) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentions, setMentions] = useState<InlineMention[]>([]);
  // Where the "@" sits, so a pick replaces the typed run rather than appending.
  const anchorStart = useRef(0);
  // The live <textarea>, taken off the events it raises.
  //
  // It is NOT passed in as a ref: the shared Textarea is a plain function
  // component under React 18, so `ref` never reaches the element and
  // `ref.current` stays null. Reading `event.target` sidesteps that without
  // changing a primitive every other composer shares.
  const elRef = useRef<HTMLTextAreaElement | null>(null);

  // Fetch only once the menu has been asked for. Shares RecipientPicker's
  // module-level cache, so opening the dialog afterwards costs no new request.
  const { sections, loading } = useRecipientSections(planId, open);

  const filtered: OptionSection[] = useMemo(
    () => (open ? filterSections(sections, query) : []),
    [open, sections, query],
  );

  // Flattened for index-based keyboard navigation; the menu still renders the
  // section headings.
  const options: RecipientOption[] = useMemo(
    () => filtered.flatMap((s) => s.items),
    [filtered],
  );

  const tokens = useMemo(() => mentions.map((m) => m.token), [mentions]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  }, []);

  const reset = useCallback(() => {
    close();
    setMentions([]);
  }, [close]);

  /**
   * Drop any typed tag whose text the author has since removed. Only tags this
   * hook inserted are considered, so dialog picks survive every edit.
   */
  const reconcile = useCallback((text: string) => {
    setMentions((prev) => {
      const kept = prev.filter((m) => text.includes(m.text));
      return kept.length === prev.length ? prev : kept;
    });
  }, []);

  /** Re-evaluate the trigger against whatever now sits left of the caret. */
  const detect = useCallback(
    (text: string, caret: number) => {
      if (disabled) return;
      const match = TRIGGER.exec(text.slice(0, caret));
      if (!match) {
        setOpen(false);
        setQuery("");
        return;
      }
      const typed = match[1] ?? "";
      anchorStart.current = caret - typed.length - 1;
      setQuery(typed);
      setActiveIndex(0);
      setOpen(true);
    },
    [disabled],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      elRef.current = e.target;
      const text = e.target.value;
      setValue(text);
      reconcile(text);
      detect(text, e.target.selectionStart ?? text.length);
    },
    [setValue, reconcile, detect],
  );

  /** Caret moved without the text changing (arrow keys, a click). */
  const handleCaretMove = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      elRef.current = el;
      detect(el.value, el.selectionStart ?? el.value.length);
    },
    [detect],
  );

  /**
   * Replace the typed "@run" with the option's text and register the token.
   *
   * The caret/refocus/autogrow dance mirrors the one SmsTemplatePicker already
   * uses in ConversationThread, so an insert behaves the same in both places.
   */
  const select = useCallback(
    (option: RecipientOption) => {
      const el = elRef.current;
      const text = el ? el.value : value;
      const caret = el?.selectionStart ?? text.length;
      const start = anchorStart.current;
      const insert = `${mentionTextFor(option)} `;
      const next = text.slice(0, start) + insert + text.slice(caret);

      setValue(next);
      setMentions((prev) =>
        prev.some((m) => m.token === option.token)
          ? prev
          : [...prev, { token: option.token, text: mentionTextFor(option) }],
      );
      close();

      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          const cursor = start + insert.length;
          el.selectionStart = el.selectionEnd = cursor;
        });
      }
    },
    [value, setValue, close],
  );

  /** Forget one tag without touching the body text. */
  const removeToken = useCallback((token: string) => {
    setMentions((prev) => prev.filter((m) => m.token !== token));
  }, []);

  /**
   * Returns true when the menu consumed the key, so the host composer can bail
   * before running its own handler — otherwise Enter would send the message
   * instead of choosing the highlighted name.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      elRef.current = e.currentTarget;
      if (!open || options.length === 0) {
        // Escape still has to be swallowed while the menu is up, but with no
        // options the menu is not up, so let it through to close the dialog.
        return false;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % options.length);
          return true;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + options.length) % options.length);
          return true;
        case "Enter":
        case "Tab": {
          e.preventDefault();
          const option = options[activeIndex];
          if (option) select(option);
          return true;
        }
        case "Escape":
          // Only reached if something dispatched Escape straight at the
          // textarea. A dialog host must ALSO intercept it via the Radix
          // onEscapeKeyDown prop, because Radix reads the key on the document
          // in the capture phase and would otherwise close before this runs.
          e.preventDefault();
          close();
          return true;
        default:
          return false;
      }
    },
    [open, options, activeIndex, select, close],
  );

  return {
    open: open && options.length > 0,
    loading,
    sections: filtered,
    options,
    activeIndex,
    setActiveIndex,
    tokens,
    mentions,
    handleChange,
    handleCaretMove,
    handleKeyDown,
    select,
    removeToken,
    reset,
    close,
  };
}
